import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Eye, ArrowLeft, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_WARNINGS = 3;

const ExamIntegrity = ({ sessionId, children, onPause }) => {
  const [violations, setViolations] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningCount, setWarningCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [awayStartTime, setAwayStartTime] = useState(null);
  const lastViolationRef = useRef(0);
  const suppressRef = useRef(false);

  // Listen for speech recorder suppression events
  useEffect(() => {
    const handleSuppress = () => { suppressRef.current = true; };
    const handleResume = () => {
      // Brief grace period after mic dialog closes
      setTimeout(() => { suppressRef.current = false; }, 2000);
    };
    window.addEventListener('exam-integrity-suppress', handleSuppress);
    window.addEventListener('exam-integrity-resume', handleResume);
    return () => {
      window.removeEventListener('exam-integrity-suppress', handleSuppress);
      window.removeEventListener('exam-integrity-resume', handleResume);
    };
  }, []);

  // Log violation to backend
  const logViolation = useCallback(async (type, details = {}) => {
    // Debounce - no more than 1 violation per second
    const now = Date.now();
    if (now - lastViolationRef.current < 1000) return;
    lastViolationRef.current = now;

    const violation = {
      type,
      details,
      timestamp: new Date().toISOString(),
    };

    setViolations(prev => [...prev, violation]);

    if (sessionId) {
      try {
        await axios.post(`${API}/exam-integrity/${sessionId}/violation`, violation);
      } catch (err) {
        // Silent fail - don't interrupt exam for logging errors
      }
    }
  }, [sessionId]);

  // 1. Tab/Window Switch Detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (suppressRef.current) return; // Skip during speech recording
        setAwayStartTime(Date.now());
        logViolation('tab_switch', { action: 'left_exam' });
        setShowWarning(true);
        setWarningCount(prev => {
          const newCount = prev + 1;
          if (newCount >= MAX_WARNINGS) {
            setIsPaused(true);
            logViolation('exam_paused', { reason: 'max_warnings_reached', warning_count: newCount });
            if (onPause) onPause();
          }
          return newCount;
        });
      } else {
        if (awayStartTime) {
          const duration = Math.round((Date.now() - awayStartTime) / 1000);
          logViolation('tab_return', { away_duration_seconds: duration });
          setAwayStartTime(null);
        }
      }
    };

    const handleBlur = () => {
      if (!document.hidden) {
        if (suppressRef.current) return; // Skip during speech recording
        // Window lost focus (e.g., Alt+Tab to another app)
        logViolation('window_blur', { action: 'window_lost_focus' });
        setShowWarning(true);
        setWarningCount(prev => {
          const newCount = prev + 1;
          if (newCount >= MAX_WARNINGS) {
            setIsPaused(true);
            logViolation('exam_paused', { reason: 'max_warnings_reached', warning_count: newCount });
            if (onPause) onPause();
          }
          return newCount;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [logViolation, onPause, awayStartTime]);

  // 2. Right-Click & Copy/Paste Blocking
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      logViolation('right_click', { target: e.target.tagName });
    };

    const handleCopy = (e) => {
      e.preventDefault();
      logViolation('copy_attempt', {});
    };

    const handlePaste = (e) => {
      // Allow paste in writing response textareas
      if (e.target.tagName === 'TEXTAREA' || e.target.getAttribute('data-allow-paste')) {
        return;
      }
      e.preventDefault();
      logViolation('paste_attempt', {});
    };

    const handleCut = (e) => {
      e.preventDefault();
      logViolation('cut_attempt', {});
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [logViolation]);

  // 3. Keyboard Shortcut Blocking
  useEffect(() => {
    const handleKeyDown = (e) => {
      const blocked = [
        // New tab/window
        (e.ctrlKey || e.metaKey) && e.key === 't',
        (e.ctrlKey || e.metaKey) && e.key === 'n',
        // Copy
        (e.ctrlKey || e.metaKey) && e.key === 'c',
        // Find
        (e.ctrlKey || e.metaKey) && e.key === 'f',
        // Dev tools
        e.key === 'F12',
        (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'i',
        (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'j',
        // Print
        (e.ctrlKey || e.metaKey) && e.key === 'p',
      ];

      if (blocked.some(Boolean)) {
        e.preventDefault();
        e.stopPropagation();
        logViolation('blocked_shortcut', { key: e.key, ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [logViolation]);

  // Dismiss warning
  const dismissWarning = () => {
    setShowWarning(false);
  };

  // Resume from pause (teacher would need to authorize in production)
  const handleResume = () => {
    setIsPaused(false);
    setWarningCount(0);
    logViolation('exam_resumed', {});
  };

  return (
    <div className="relative" data-testid="exam-integrity-wrapper">
      {/* Integrity status bar */}
      <div className="bg-gray-900 text-white px-4 py-2 flex items-center justify-between text-xs no-print" data-testid="integrity-status-bar">
        <div className="flex items-center gap-2">
          <Shield size={14} className={warningCount > 0 ? 'text-yellow-400' : 'text-green-400'} />
          <span>Exam Monitor Active</span>
        </div>
        {warningCount > 0 && (
          <div className="flex items-center gap-1 text-yellow-400" data-testid="warning-counter">
            <AlertTriangle size={14} />
            <span>{warningCount}/{MAX_WARNINGS} warnings</span>
          </div>
        )}
      </div>

      {/* Warning Overlay */}
      {showWarning && !isPaused && (
        <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center" data-testid="tab-switch-warning">
          <div className="bg-white border-2 border-yellow-500 p-8 max-w-md w-full mx-4 text-center">
            <div className="w-16 h-16 bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Please Stay on the Exam
            </h2>
            <p className="text-gray-600 mb-2">
              Leaving the exam window has been detected and recorded.
            </p>
            <p className="text-sm text-yellow-700 font-semibold mb-6">
              Warning {warningCount} of {MAX_WARNINGS} — After {MAX_WARNINGS} warnings, the exam will be paused.
            </p>
            <Button onClick={dismissWarning} className="btn-sharp-primary h-12 px-8" data-testid="dismiss-warning-btn">
              <ArrowLeft size={18} className="mr-2" />
              Return to Exam
            </Button>
          </div>
        </div>
      )}

      {/* Paused Overlay */}
      {isPaused && (
        <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center" data-testid="exam-paused-overlay">
          <div className="bg-white border-2 border-red-500 p-8 max-w-md w-full mx-4 text-center">
            <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Eye size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Exam Paused
            </h2>
            <p className="text-gray-600 mb-2">
              Your exam has been paused due to multiple instances of leaving the exam window.
            </p>
            <p className="text-sm text-gray-500 mb-2">
              This has been recorded and will be visible to your teacher.
            </p>
            <p className="text-sm text-red-600 font-semibold mb-6">
              {violations.filter(v => v.type === 'tab_switch' || v.type === 'window_blur').length} tab switches detected
            </p>
            <Button onClick={handleResume} className="btn-sharp bg-red-600 hover:bg-red-700 text-white h-12 px-8" data-testid="resume-exam-btn">
              Resume Exam
            </Button>
          </div>
        </div>
      )}

      {/* Exam Content */}
      <div className={isPaused ? 'filter blur-lg pointer-events-none select-none' : 'select-none'}>
        {children}
      </div>
    </div>
  );
};

export default ExamIntegrity;
